import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarClock,
  CheckCircle2,
  Clock,
  Database,
  FileText,
  Server,
  type LucideIcon,
} from "lucide-react"
import { Link } from "react-router-dom"
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

import { IconTile, Panel, Tag, fmtDateBR, type Tone } from "@/components/superadmin/kit"
import { useCondominiums } from "@/hooks/useCondominiums"
import { useProfiles } from "@/hooks/useProfiles"
import { supabase } from "@/integrations/supabase/client"
import { cn } from "@/lib/utils"

export type OverviewPeriod = "month" | "quarter" | "year"

const currency = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 })

export const ORG_TYPE_LABEL: Record<string, string> = {
  condominio: "Condomínio",
  ponto_eletronico: "Ponto eletrônico",
  controle_acessos: "Controle de acessos",
}

export const PAYMENT_STATUS: Record<string, { label: string; tone: Tone }> = {
  active: { label: "Em dia", tone: "success" },
  pending: { label: "Pendente", tone: "warning" },
  overdue: { label: "Em atraso", tone: "danger" },
  suspended: { label: "Suspensa", tone: "neutral" },
}

function ActionRow({ to, icon, tone, label, detail, count }: { to: string; icon: LucideIcon; tone: Tone; label: string; detail?: string; count: number }) {
  const idle = count === 0
  return (
    <Link to={to} className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-[hsl(228_8%_10%)]">
      <IconTile icon={icon} tone={idle ? "neutral" : tone} />
      <div className="min-w-0 flex-1">
        <div className={cn("truncate text-[13px] font-medium", idle ? "text-muted-foreground" : "text-foreground")}>{label}</div>
        {detail && <div className="truncate text-[12px] text-muted-foreground">{detail}</div>}
      </div>
      <span className={cn("text-[13px] font-semibold tabular-nums", idle ? "text-muted-foreground" : "text-foreground")}>{count}</span>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  )
}

const PlatformOverview = ({ period = "month" }: { period?: OverviewPeriod }) => {
  const { data: condominiums = [], isLoading: loadingCondominiums } = useCondominiums()
  const { data: profiles = [], isLoading: loadingProfiles } = useProfiles()
  const { data: proposals = [], isLoading: loadingProposals } = useQuery({
    queryKey: ["propostas-superadmin-overview"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("propostas")
        .select("id, titulo, cliente_nome, cliente_empresa, status, valor_final, created_at")
        .order("created_at", { ascending: false })
      if (error) throw error
      return data || []
    },
  })

  const { data: contracts = [] } = useQuery({
    queryKey: ["contratos-superadmin-overview"],
    queryFn: async () => {
      const { data, error } = await supabase.from("contratos").select("id, proposta_id, cliente_nome, cliente_empresa, status")
      if (error) throw error
      return data || []
    },
  })

  const userGrowth = useMemo(() => {
    const today = new Date()
    const countBetween = (start: Date, end: Date) =>
      profiles.filter((p) => p.created_at && new Date(p.created_at) >= start && new Date(p.created_at) < end).length

    if (period === "year") {
      return Array.from({ length: 5 }, (_, i) => {
        const year = today.getFullYear() - (4 - i)
        return { label: String(year), users: countBetween(new Date(year, 0, 1), new Date(year + 1, 0, 1)) }
      })
    }
    if (period === "quarter") {
      return Array.from({ length: 6 }, (_, i) => {
        const idx = today.getFullYear() * 4 + Math.floor(today.getMonth() / 3) - (5 - i)
        const year = Math.floor(idx / 4)
        const q = ((idx % 4) + 4) % 4
        return { label: `${q + 1}T/${String(year).slice(-2)}`, users: countBetween(new Date(year, q * 3, 1), new Date(year, q * 3 + 3, 1)) }
      })
    }
    return Array.from({ length: 8 }, (_, i) => {
      const month = new Date(today.getFullYear(), today.getMonth() - (7 - i), 1)
      return {
        label: month.toLocaleDateString("pt-BR", { month: "short" }).replace(".", ""),
        users: countBetween(month, new Date(month.getFullYear(), month.getMonth() + 1, 1)),
      }
    })
  }, [period, profiles])

  const overdue = condominiums.filter((c) => c.payment_status === "overdue")
  const expiringSoon = condominiums.filter((c) => {
    if (!c.subscription_expires_at) return false
    const exp = new Date(c.subscription_expires_at)
    const limit = new Date()
    limit.setDate(limit.getDate() + 30)
    return exp >= new Date() && exp <= limit
  })
  // Uma proposta aceita só "precisa de contrato" se nenhum contrato aponta para ela.
  // Contratos antigos podem não ter proposta_id; nesse caso casa pelo cliente.
  const norm = (v?: string | null) => (v || "").trim().toLowerCase()
  const accepted = proposals.filter((p) => p.status === "aceita")
  const awaitingContract = accepted.filter((p) =>
    !contracts.some((c) =>
      c.status !== "cancelado" &&
      (c.proposta_id === p.id || (!c.proposta_id && norm(c.cliente_nome) === norm(p.cliente_nome) && norm(c.cliente_empresa) === norm(p.cliente_empresa))),
    ),
  )
  const sent = proposals.filter((p) => p.status === "enviada")
  const totalUsers = userGrowth.reduce((t, i) => t + i.users, 0)
  const recentOrgs = condominiums.slice(0, 5)
  const isLoading = loadingCondominiums || loadingProfiles || loadingProposals

  if (isLoading) {
    return (
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="h-80 animate-pulse rounded-xl border border-border bg-card" />
        <div className="h-80 animate-pulse rounded-xl border border-border bg-card" />
      </div>
    )
  }

  return (
    <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className="min-w-0 space-y-3">
        <Panel
          title="Novos usuários"
          description="Cadastros por período"
          actions={
            <div className="text-right">
              <div className="text-[18px] font-semibold tabular-nums text-foreground">{totalUsers}</div>
              <div className="text-[11px] text-muted-foreground">no intervalo</div>
            </div>
          }
          bodyClassName="px-2 pb-2 pt-4"
        >
          <div className="h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={userGrowth} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="saUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(224 100% 64%)" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="hsl(224 100% 64%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="hsl(228 8% 14%)" />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: "hsl(225 6% 50%)", fontSize: 11 }} />
                <YAxis width={36} allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "hsl(225 6% 50%)", fontSize: 11 }} />
                <Tooltip
                  cursor={{ stroke: "hsl(228 8% 24%)" }}
                  contentStyle={{ background: "hsl(228 9% 9%)", border: "1px solid hsl(228 8% 17%)", borderRadius: 8, color: "hsl(225 10% 93%)", fontSize: 12 }}
                  labelStyle={{ color: "hsl(225 6% 60%)" }}
                  formatter={(v: number) => [v, "Novos usuários"]}
                />
                <Area type="monotone" dataKey="users" stroke="hsl(224 100% 64%)" strokeWidth={2} fill="url(#saUsers)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel
          title="Organizações recentes"
          actions={<Link to="/superadmin/organizacoes" className="text-[12px] font-medium text-[hsl(224_100%_76%)] hover:underline">Ver todas</Link>}
          flush
        >
          {recentOrgs.length === 0 ? (
            <div className="px-4 py-8 text-center text-[13px] text-muted-foreground">Nenhuma organização cadastrada.</div>
          ) : (
            <ul className="divide-y divide-border">
              {recentOrgs.map((org) => {
                const status = PAYMENT_STATUS[org.payment_status] ?? PAYMENT_STATUS.active
                return (
                  <li key={org.id} className="flex items-center gap-3 px-4 py-3">
                    <IconTile icon={Building2} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-medium text-foreground">{org.name}</div>
                      <div className="truncate text-[12px] text-muted-foreground">{ORG_TYPE_LABEL[org.type] ?? "Condomínio"} · desde {fmtDateBR(org.created_at)}</div>
                    </div>
                    <Tag tone={status.tone} dot>{status.label}</Tag>
                  </li>
                )
              })}
            </ul>
          )}
        </Panel>
      </div>

      <div className="min-w-0 space-y-3">
        <Panel title="Precisa de você" flush>
          <div className="divide-y divide-border">
            <ActionRow
              to="/gestao-comercial/propostas"
              icon={CheckCircle2}
              tone="success"
              label={awaitingContract.length === 1 ? "Proposta aceita sem contrato" : "Propostas aceitas sem contrato"}
              detail={awaitingContract[0] ? `${awaitingContract[0].cliente_empresa || awaitingContract[0].cliente_nome} · ${currency.format(Number(awaitingContract[0].valor_final || 0))}` : "Todas já têm contrato"}
              count={awaitingContract.length}
            />
            <ActionRow
              to="/gestao-comercial/propostas"
              icon={Clock}
              tone="accent"
              label="Propostas aguardando resposta"
              detail={sent.length ? currency.format(sent.reduce((t, p) => t + Number(p.valor_final || 0), 0)) + " em negociação" : "Nenhuma em aberto"}
              count={sent.length}
            />
            <ActionRow to="/superadmin/organizacoes" icon={AlertTriangle} tone="danger" label="Pagamentos em atraso" count={overdue.length} />
            <ActionRow to="/superadmin/organizacoes" icon={CalendarClock} tone="warning" label="Assinaturas vencendo em 30 dias" count={expiringSoon.length} />
          </div>
        </Panel>

        <Panel title="Saúde do sistema" actions={<Tag tone="success" dot>Operacional</Tag>} flush>
          <ul className="divide-y divide-border text-[13px]">
            {([
              [Server, "Aplicação", "Respondendo"],
              [Database, "Banco de dados", "Conectado"],
              [FileText, "Propostas públicas", "Links ativos"],
            ] as const).map(([Icon, label, value]) => (
              <li key={label} className="flex items-center gap-3 px-4 py-2.5">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <span className="flex-1 text-foreground">{label}</span>
                <span className="text-[12px] text-muted-foreground">{value}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-border p-3">
            <Link to="/superadmin/dados" className="flex items-center justify-center gap-1.5 rounded-md py-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-[hsl(228_8%_11%)] hover:text-foreground">
              Ver dados e capacidade <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Panel>
      </div>
    </div>
  )
}

export { PlatformOverview }
